import { invokeApi } from '../bl_libs/invokeApi';
import { LOCAL_STORAGE_KEYS, FEATURE_KEY } from '../utils/constants';
import { normaliseDashboardResponse } from '../utils/suppression';

const getToken = () => JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));

// Every dashboard payload passes through here (the context AND the pages that
// fetch directly, e.g. School › Program Impact), so this is where the n < 10
// suppression rule is applied: flagged scores are nulled before anything can
// draw them — see utils/suppression.js.
const getDashboard = (path) => invokeApi({
    path,
    method: 'GET',
    headers: { Authorization: `Bearer ${getToken()}` },
}).then(normaliseDashboardResponse);

export const getSchoolDashboardService = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return getDashboard(`/dq_insider/dynamic_dashboard/school${query ? `?${query}` : ''}`);
};

export const getPartnerDashboardService = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return getDashboard(`/dq_insider/dynamic_dashboard/partner${query ? `?${query}` : ''}`);
};

export const getClassDashboardService = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return getDashboard(`/dq_insider/dynamic_dashboard/class${query ? `?${query}` : ''}`);
};

export const getIndividualDashboardService = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return getDashboard(`/dq_insider/dynamic_dashboard/individual${query ? `?${query}` : ''}`);
};

export const getNationalDashboardService = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return getDashboard(`/dq_insider/dynamic_dashboard/national${query ? `?${query}` : ''}`);
};

// One National dashboard per partner × country: the partner's countries [{country, uuid}] for the
// workspace menu (Insights › National Dashboard › one entry per country). Completed assessments only.
export const getNationalDashboardCountriesService = () => {
    return invokeApi({
        path: '/dq_insider/dynamic_dashboard/national/countries',
        method: 'GET',
        headers: { Authorization: `Bearer ${getToken()}` },
    });
};

export const getSchoolNetworkDashboardService = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return getDashboard(`/dq_insider/dynamic_dashboard/school_network${query ? `?${query}` : ''}`);
};

export const getProgramImpactDashboardService = (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return getDashboard(`/dq_insider/dynamic_dashboard/program_impact${query ? `?${query}` : ''}`);
};

/**
 * LC Teacher program picker — handover "Dynamic Dashboards — Frontend API Handover" 1.1 §3.
 * Resolves to `{ error, error_message, data: { programs: [{ code, name, created_at, has_dashboard, dashboards[] }] } }`.
 * Calls invokeApi directly, not getDashboard: the list is not a dashboard payload and
 * normaliseDashboardResponse would inject a `registry` key into it.
 */
export const getProgramImpactProgramsService = () => {
    return invokeApi({
        path: '/dq_insider/dynamic_dashboard/program_impact/programs',
        method: 'GET',
        headers: { Authorization: `Bearer ${getToken()}` },
    });
};

// Partner workspace (PW) document for Growth Opportunities: resolves to `{ data: <PW document> | null }`,
// values under data.data.*. invokeApi directly — not a dashboard payload, so no normaliseDashboardResponse.
export const getPartnerWorkspaceService = () => {
    return invokeApi({
        path: '/dq_insider/workspace/get?workspace_type=PW',
        method: 'GET',
        headers: { Authorization: `Bearer ${getToken()}` },
    });
};

// Dispatcher for backward compatibility with any direct getDynamicDashboardService callers
export const getDynamicDashboardService = (params = {}) => {
    const { dashboard_type, section_code, module_code, ...rest } = params;
    switch (dashboard_type) {
        case FEATURE_KEY.SCHOOL_DASHBOARD:
            return getSchoolDashboardService(rest);
        case FEATURE_KEY.PARTNER_DASHBOARD:
            return getPartnerDashboardService(rest);
        case FEATURE_KEY.CLASS_DASHBOARD:
            return getClassDashboardService({ ...(section_code ? { section_code } : {}), ...rest });
        case FEATURE_KEY.INDIVIDUAL_DASHBOARD:
            return getIndividualDashboardService(rest);
        case FEATURE_KEY.NATIONAL_DASHBOARD:
            return getNationalDashboardService(rest);
        case FEATURE_KEY.SCHOOL_NETWORK_DASHBOARD:
            return getSchoolNetworkDashboardService(rest);
        case FEATURE_KEY.PROGRAM_IMPACT_DASHBOARD:
            return getProgramImpactDashboardService({ ...(module_code ? { module_code } : {}), ...rest });
        default: {
            const query = new URLSearchParams(params).toString();
            return getDashboard(`/dq_insider/dynamic_dashboard/get${query ? `?${query}` : ''}`);
        }
    }
};
